import React, { useState, useEffect } from "react";
import { Card, Table, Tag, Empty, Button, Tooltip, Space } from "antd";
import { 
    SyncOutlined, 
    CheckCircleOutlined, 
    CloseCircleOutlined, 
    ClockCircleOutlined,
    WifiOutlined,
    DisconnectOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn"; // 预加载中文 locale，但不全局设置

dayjs.extend(relativeTime);
// 注意：不再调用 dayjs.locale("zh-cn")，避免污染全局 locale
// 需要中文输出时使用实例级 .locale("zh-cn") 调用

/**
 * 连接状态监控组件
 * 显示所有展示页客户端的在线/离线状态
 */
const ConnectionMonitor = ({ dataSource = [], loading = false, onRefresh }) => {
    const columns = [
        {
            title: "状态",
            key: "status",
            width: 80,
            fixed: "left",
            render: (_, record) => {
                const isOnline = record.is_online;
                return (
                    <Tooltip title={isOnline ? "在线" : "离线"}>
                        {isOnline ? (
                            <CheckCircleOutlined style={{ fontSize: 20, color: "#52c41a" }} />
                        ) : (
                            <CloseCircleOutlined style={{ fontSize: 20, color: "#d9d9d9" }} />
                        )}
                    </Tooltip>
                );
            },
        },
        {
            title: "客户端ID",
            dataIndex: "client_id",
            key: "client_id",
            width: 200,
            render: (clientId, record) => {
                const isOnline = record.is_online;
                return (
                    <Space>
                        {isOnline ? (
                            <WifiOutlined style={{ color: "#52c41a" }} />
                        ) : (
                            <DisconnectOutlined style={{ color: "#d9d9d9" }} />
                        )}
                        <span style={{ 
                            fontWeight: 500,
                            color: isOnline ? "#262626" : "#999"
                        }}>
                            {clientId}
                        </span>
                    </Space>
                );
            },
        },
        {
            title: "连接状态",
            key: "connection_status",
            width: 100,
            render: (_, record) => {
                const isOnline = record.is_online;
                return (
                    <Tag 
                        color={isOnline ? "success" : "default"}
                        icon={isOnline ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    >
                        {isOnline ? "在线" : "离线"}
                    </Tag>
                );
            },
        },
        {
            title: "连接时间",
            dataIndex: "connected_at",
            key: "connected_at",
            width: 180,
            render: (time) => {
                const connectedTime = dayjs(time);
                return (
                    <Tooltip title={connectedTime.format("YYYY-MM-DD HH:mm:ss")}>
                        <Space>
                            <ClockCircleOutlined style={{ color: "#1890ff" }} />
                            <span>{connectedTime.locale("zh-cn").fromNow()}</span>
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: "最后心跳",
            key: "heartbeat",
            width: 180,
            render: (_, record) => {
                const lastHeartbeat = dayjs(record.last_heartbeat);
                const secondsSince = record.seconds_since_heartbeat;
                const isRecent = secondsSince < 30;
                
                return (
                    <Tooltip 
                        title={`${lastHeartbeat.format("YYYY-MM-DD HH:mm:ss")} (${secondsSince}秒前)`}
                    >
                        <Space>
                            <ClockCircleOutlined 
                                style={{ color: isRecent ? "#52c41a" : "#faad14" }} 
                            />
                            <span style={{ color: isRecent ? "#52c41a" : "#faad14" }}>
                                {secondsSince < 60 
                                    ? `${secondsSince}秒前` 
                                    : lastHeartbeat.locale("zh-cn").fromNow()
                                }
                            </span>
                        </Space>
                    </Tooltip>
                );
            },
        },
        {
            title: "在线时长",
            key: "duration",
            width: 150,
            render: (_, record) => {
                const connectedTime = dayjs(record.connected_at);
                const duration = dayjs().diff(connectedTime, "second");
                
                let displayText;
                if (duration < 60) {
                    displayText = `${duration}秒`;
                } else if (duration < 3600) {
                    const minutes = Math.floor(duration / 60);
                    displayText = `${minutes}分钟`;
                } else {
                    const hours = Math.floor(duration / 3600);
                    const minutes = Math.floor((duration % 3600) / 60);
                    displayText = `${hours}小时${minutes}分钟`;
                }
                
                return (
                    <Tooltip title={`在线${displayText}`}>
                        <span style={{ color: "#666" }}>{displayText}</span>
                    </Tooltip>
                );
            },
        },
    ];

    // 确保dataSource是数组，并统计在线和离线数量
    const clientsList = Array.isArray(dataSource) ? dataSource : [];
    const onlineCount = clientsList.filter(client => client.is_online).length;
    const offlineCount = clientsList.length - onlineCount;

    return (
        <Card
            title={
                <Space>
                    <span>展示页连接状态</span>
                    <Tag color="success" icon={<CheckCircleOutlined />}>
                        在线: {onlineCount}
                    </Tag>
                    {offlineCount > 0 && (
                        <Tag color="default" icon={<CloseCircleOutlined />}>
                            离线: {offlineCount}
                        </Tag>
                    )}
                </Space>
            }
            extra={
                <Button
                    icon={<SyncOutlined spin={loading} />}
                    onClick={onRefresh}
                    loading={loading}
                    size="small"
                >
                    刷新
                </Button>
            }
            size="small"
        >
            <Table
                columns={columns}
                dataSource={clientsList}
                loading={loading}
                rowKey="client_id"
                pagination={false}
                size="small"
                rowClassName={(record) => record.is_online ? "" : "offline-row"}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <div>
                                    <p style={{ marginBottom: 8 }}>暂无展示页连接</p>
                                    <p style={{ color: "#999", fontSize: 12 }}>
                                        展示页客户端连接后将显示在此处
                                    </p>
                                </div>
                            }
                        />
                    ),
                }}
            />
        </Card>
    );
};

export default ConnectionMonitor;


